import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

// Layouts
import MainLayout from "layouts/Main";

// Components
import { Button } from "@octopus-investments/deep-sea-components";

// Actions
import { setContent, setEmailNotSent } from "store/slices/applicationSlice";
import { setResendButtonLoader } from "store/slices/loadersSlice";
import { sendNewEmail } from "store/slices/accountSlice";

// Tools
import { ADVISER_PHONE, INVESTOR_PHONE } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./CheckEmailMessagePage.scss";

export default function CheckEmailMessagePage() {
  const [status, setStatus] = useState("Check your email");

  const {
    backButtonTarget,
    email,
    isEmailNotSent,
    token: isHideBackButton,
  } = useSelector(({ application }) => application);
  const { isResendButtonLoader } = useSelector(({ loaders }) => loaders);

  // Redux
  const reduxDispatch = useDispatch();

  function handleBackButton() {
    ampli.backButtonClicked({
      di_email_entered: email,
      location: "di_check_your_email_page",
    });

    reduxDispatch(setContent(backButtonTarget));
  }

  function handleResendButton() {
    if (isResendButtonLoader) return;

    reduxDispatch(setResendButtonLoader(true));
    reduxDispatch(setEmailNotSent(false));

    const returnUrl = new URLSearchParams(window.location.search).get("returnUrl");

    reduxDispatch(sendNewEmail({ returnUrl })).then(result => {
      const { success } = result.payload;

      ampli.resendEmailClicked({
        di_email_entered: email,
      });

      if (success) {
        setStatus("A new link has been sent to your email");
      } else {
        reduxDispatch(setEmailNotSent(true));
        return reduxDispatch(setResendButtonLoader(false));
      }
    });
  }

  const renderButtonsBlock = () => {
    return (
      <div className={classNames("buttons-block", isHideBackButton && "no-back-button")}>
        {!isHideBackButton && <Button onClick={handleBackButton} label="Back" variant="outline" />}
        <Button
          isLoading={isResendButtonLoader}
          onClick={handleResendButton}
          label="Resend"
          variant="solid"
        />
      </div>
    );
  };

  const renderErrorBlock = () => {
    return (
      <>
        <h1 className="coheadline">Email not sent</h1>
        <div className="information-block">
          <p>
            Unfortunately, your email could not be sent. Please try again by clicking 'Resend'
            below.
          </p>
          <p>
            If this still doesn't work, get in touch. If you’re an investor please call{" "}
            {INVESTOR_PHONE}. If you’re an adviser please call {ADVISER_PHONE}.
          </p>
        </div>
        {renderButtonsBlock()}
      </>
    );
  };

  const renderDefaultBlock = () => {
    return (
      <>
        <h1 className="coheadline">{status}</h1>

        <p className="copy">
          We've emailed you a link to create a secure password for your online account. This should
          arrive within five minutes. Please note, the link will expire after 12 hours.
        </p>

        {renderButtonsBlock()}

        <div className="password-criteria-block">
          <p>Your password must contain:</p>
          <ul>
            <li>14 characters or more</li>
            <li>At least one lower case letter</li>
            <li>At least one upper case letter</li>
            <li>At least one number</li>
          </ul>
        </div>
      </>
    );
  };

  return (
    <MainLayout>
      <div className="check-email-content">
        {isEmailNotSent ? renderErrorBlock() : renderDefaultBlock()}
      </div>
    </MainLayout>
  );
}
